import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaPuzzlePiece } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import './Extensions.css';

// ══════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════
const API = 'https://uat.definiteassurance.com/api';
const COMPANY = 'Definite';
const ROWS_PER_PAGE = 20;

// ══════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════
const formatISODate = (input) => {
  try {
    if (typeof input === 'string') return input.split('T')[0];
    if (input instanceof Date) return input.toISOString().split('T')[0];
    return '--';
  } catch { return '--'; }
};

const formatter = (amount) =>
  Number(amount || 0).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');

/** Build a smart page-number array with ellipses for large sets */
const buildPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push('...');
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < total - 1) pages.push('...');
  pages.push(total);
  return pages;
};

// ══════════════════════════════════════
//  COMPONENT
// ══════════════════════════════════════
export default function Extensions() {
  const navigate = useNavigate();

  // ── Auth & agent ──
  const [credentials, setCredentials] = useState(null);
  const [isAuth, setIsauth] = useState(false);
  const [agent, setAgent] = useState('');
  const [agentCode, setAgentcode] = useState('');
  const [agentName, setAgentname] = useState('');

  // ── Data ──
  const [extensions, setExtensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // ══════════════════════════════════════
  //  AUTH HEADERS
  // ══════════════════════════════════════
  const authHeaders = useCallback(() => {
    const base = { 'Content-Type': 'application/json' };
    return credentials ? { ...base, Authorization: `Basic ${credentials}` } : base;
  }, [credentials]);

  // ══════════════════════════════════════
  //  INITIAL DATA FETCHING
  // ══════════════════════════════════════

  // Credentials
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/getCredentials?username=${COMPANY}`);
        const data = await res.json();
        setCredentials(btoa(`${data.Username}:${data.Password}`));
        setIsauth(true);
      } catch (e) {
        console.error('Failed to get credentials', e);
        toast.error('Authentication failed');
      }
    })();
  }, []);

  // Agent from localStorage
  useEffect(() => {
    const user = localStorage.getItem('definiteagent');
    if (user) setAgent(user);
    else navigate('/login');
  }, [navigate]);

  // Agent details
  useEffect(() => {
    if (!agent || !isAuth) return;
    (async () => {
      try {
        const res = await fetch(`${API}/getDealerdetails?email=${agent}`, {
          method: 'GET', headers: authHeaders()
        });
        const data = await res.json();
        setAgentcode(data.OID);
        setAgentname(data.Dealer_name || '');
      } catch (e) {
        console.error('getDealerdetails error', e);
      }
    })();
  }, [agent, isAuth, authHeaders]);

  // Extensions data
  useEffect(() => {
    if (!agentCode || !isAuth) return;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`${API}/getExtensions?agent=${agentCode}`, {
          method: 'GET', headers: authHeaders()
        });
        const data = await res.json();
        setExtensions(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('getExtensions error', e);
        toast.error('Failed to load extensions');
        setExtensions([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [agentCode, isAuth, authHeaders]);

  // ══════════════════════════════════════
  //  FILTERED & PAGINATED DATA
  // ══════════════════════════════════════
  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return extensions;
    const q = searchTerm.toLowerCase();
    return extensions.filter(v =>
      (v.DocumentNo || '').toLowerCase().includes(q) ||
      (v.PolicyNo || '').toLowerCase().includes(q) ||
      (v.Dealer_name || '').toLowerCase().includes(q) ||
      (v.ProductDescription || '').toLowerCase().includes(q) ||
      (v.Narration || '').toLowerCase().includes(q)
    );
  }, [extensions, searchTerm]);

  // Reset to page 1 when search changes
  useEffect(() => { setCurrentPage(1); }, [searchTerm]);

  const totalPages = Math.ceil(filtered.length / ROWS_PER_PAGE);
  const indexOfFirst = (currentPage - 1) * ROWS_PER_PAGE;
  const indexOfLast = indexOfFirst + ROWS_PER_PAGE;
  const currentRows = filtered.slice(indexOfFirst, indexOfLast);
  const pageNumbers = useMemo(() => buildPageNumbers(currentPage, totalPages), [currentPage, totalPages]);

  // ══════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════
  return (
    <div className="ext-page">
      <Sidebar />
      <div className="ext-main">
        <Header userName={agentName || agent || 'Agent'} />
        <ToastContainer position="top-right" autoClose={4000} />

        {/* Top bar */}
        <div className="ext-top-row">
          <span className="ext-company">Definite Assurance</span>
          <span className="ext-breadcrumb">Dashboard / Extension Management</span>
        </div>

        <div className="ext-card">
          <h3><FaPuzzlePiece style={{ marginRight: 8 }} /> Extension Management</h3>

          {/* Search & stats */}
          <div className="ext-filter-bar">
            <div className="ext-search-wrap">
              <FaSearch className="ext-search-icon" />
              <input
                type="text"
                placeholder="Search by note#, policy#, client, product, registration…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <span className="ext-stat">
              Total: <strong>{extensions.length}</strong>
              {searchTerm && <> &nbsp;|&nbsp; Found: <strong>{filtered.length}</strong></>}
            </span>
          </div>

          {/* Loading */}
          {loading && (
            <div className="ext-loading">
              <div className="ext-spinner" />
              <p>Loading extensions…</p>
            </div>
          )}

          {/* Empty state */}
          {!loading && filtered.length === 0 && (
            <div className="ext-empty">
              <FaPuzzlePiece size={32} color="#ccc" style={{ marginBottom: 10 }} />
              <p>{searchTerm ? 'No extensions match your search.' : 'No extensions found.'}</p>
            </div>
          )}

          {/* Table */}
          {!loading && filtered.length > 0 && (
            <>
              <div className="ext-table-wrap">
                <table className="ext-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Note No#</th>
                      <th>Policy No.</th>
                      <th>Date Done</th>
                      <th>Client</th>
                      <th>Product</th>
                      <th>Registration</th>
                      <th>Commencement</th>
                      <th>Expiry</th>
                      <th>Gross Premium (KES)</th>
                      <th>Net Commission (KES)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRows.map((val, index) => (
                      <tr key={val.OID || index}>
                        <td>{indexOfFirst + index + 1}</td>
                        <td>
                          <button className="ext-note-btn" value={val.OID}>
                            {val.DocumentNo}
                          </button>
                        </td>
                        <td>{val.PolicyNo}</td>
                        <td>{formatISODate(val.DateDone)}</td>
                        <td>{val.Dealer_name}</td>
                        <td>{val.ProductDescription}</td>
                        <td>{val.Narration}</td>
                        <td>{val.CommencementDate ? formatISODate(val.CommencementDate) : '--'}</td>
                        <td>{val.ExpiryDate ? formatISODate(val.ExpiryDate) : '--'}</td>
                        <td>{formatter(val.GrossPremium)}</td>
                        <td>{formatter(val.NetCommission)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="ext-pagination">
                <span className="ext-pagination-info">
                  Showing {indexOfFirst + 1} to {Math.min(indexOfLast, filtered.length)} of {filtered.length} entries
                </span>
                <div className="ext-pagination-controls">
                  <button
                    className="ext-page-btn"
                    onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  {pageNumbers.map((page, i) =>
                    page === '...'
                      ? <span key={`e${i}`} className="ext-page-ellipsis">…</span>
                      : (
                        <button
                          key={page}
                          className={`ext-page-btn ${currentPage === page ? 'active' : ''}`}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      )
                  )}
                  <button
                    className="ext-page-btn"
                    onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages || totalPages === 0}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
